import { Avatar, Box, Group, Stack, UnstyledButton } from '@mantine/core';
import { useSuspenseRetrievePrompt } from '@repo/api-codegen';
import { AIAvatar } from '@repo/common/components/AI/Avatar';
import { IconButton, Text } from '@repo/foundations';
import { memo, Suspense, useMemo } from 'react';
import { Link } from 'react-router';
import { AiQualityBadgeController } from '../AiQualityBadge';
import { AiChatEmbedPreviewBody } from './components/AiChatEmbedPreviewBody';
import { AiChatEmbedPreviewSkeleton } from './components/AiChatEmbedPreviewSkeleton';

interface AiChatEmbedPreviewControllerProps {
	promptId: string;
	onRemove?: () => void;
}

function AiChatEmbedPreviewControllerInternal({
	promptId,
	onRemove,
}: AiChatEmbedPreviewControllerProps) {
	const { data } = useSuspenseRetrievePrompt({
		pathParams: {
			promptId,
		},
	});

	const lastAssistantMessage = useMemo(() => {
		const response = data.structured_response?.findLast(
			(result) => result.sender === 'assistant' && !result.thinking
		);

		if (response?.content?.length) {
			return response.content;
		}

		const lastMessage =
			data.structured_response?.[data.structured_response.length - 1];

		return lastMessage?.content ?? 'No response content found';
	}, [data.structured_response]);

	return (
		<UnstyledButton component={Link} to={`/ai/${data.id}`}>
			<AiChatEmbedPreviewBody>
				<Group spacing="sm" noWrap>
					{data.persona?.avatar_url ? (
						<Avatar
							src={data.persona.avatar_url}
							size="md"
							alt={`${data.persona.name} avatar`}
						/>
					) : (
						<AIAvatar size="md" speed="none" />
					)}
					<Stack spacing={0} miw={0} style={{ flex: 1 }}>
						<Text size="sm" weight="semibold">
							Message from {data.persona?.name ?? 'Secoda AI'}
						</Text>
						<Text size="xs" color="text/secondary/default" lineClamp={1}>
							{lastAssistantMessage}
						</Text>
					</Stack>
					{data.benchmark_raw?.score && (
						<Box style={{ flexShrink: 0 }}>
							<AiQualityBadgeController score={data.benchmark_raw.score} />
						</Box>
					)}
					{onRemove && (
						<IconButton iconName="x" variant="tertiary" onClick={onRemove} />
					)}
				</Group>
			</AiChatEmbedPreviewBody>
		</UnstyledButton>
	);
}

export const AiChatEmbedPreviewController = memo(
	(props: AiChatEmbedPreviewControllerProps) => (
		<Suspense fallback={<AiChatEmbedPreviewSkeleton />}>
			<AiChatEmbedPreviewControllerInternal {...props} />
		</Suspense>
	)
);
